import Papa from 'papaparse';
import { Transaction } from '../types';

interface ColumnMapping {
  date: string;
  description: string;
  amount?: string;
  credit?: string;
  debit?: string;
  type?: string;
  balance?: string;
  checkNumber?: string;
  category?: string;
}

export interface ParseProgress {
  percent: number;
  status: 'parsing' | 'processing' | 'complete' | 'error';
  message: string;
}

// Common header mappings for different CSV formats
const HEADER_MAPPINGS: Record<string, string> = {
  // Date variations
  'posting date': 'date',
  'transaction date': 'date',
  'date': 'date',
  'transaction_date': 'date',
  'posting_date': 'date',
  
  // Description variations
  'description': 'description',
  'details': 'description',
  'transaction description': 'description',
  'memo': 'description',
  'notes': 'description',
  'transaction_details': 'description',
  
  // Amount variations
  'amount': 'amount',
  'transaction amount': 'amount',
  'debit/credit': 'amount',
  'debit': 'amount',
  'credit': 'amount',
  'transaction_amount': 'amount',
  
  // Type variations
  'type': 'type',
  'transaction type': 'type',
  'transaction_type': 'type',
  
  // Balance variations
  'balance': 'balance',
  'running balance': 'balance',
  'account balance': 'balance',
  'running_balance': 'balance',
  
  // Check/Slip variations
  'check or slip #': 'checkNumber',
  'check number': 'checkNumber',
  'check_no': 'checkNumber',
  'check#': 'checkNumber',
  'slip#': 'checkNumber',
};

// Normalize header names
function normalizeHeader(header: string): string {
  const normalized = header.toLowerCase().trim();
  return HEADER_MAPPINGS[normalized] || normalized;
}

// Safely get value from row with type checking
function getRowValue(row: Record<string, string>, key: string | undefined): string {
  if (!key || !row[key]) return '';
  return row[key].trim();
}

export async function getCSVHeaders(file: File): Promise<string[]> {
  return new Promise((resolve, reject) => {
    Papa.parse(file, {
      header: true,
      preview: 1,
      skipEmptyLines: true,
      transformHeader: (header) => header.trim(),
      transform: (value) => value.trim(),
      complete: (results) => {
        // Check for parsing errors
        if (results.errors.length > 0) {
          // Log the error but don't reject if it's just a field count mismatch
          const isFieldCountError = results.errors.some(e => 
            e.message.includes('Too many fields') || 
            e.message.includes('Expected') && e.message.includes('fields')
          );
          
          if (!isFieldCountError) {
            console.error('CSV parsing errors:', results.errors);
            reject(new Error(`Error parsing CSV: ${results.errors[0].message}`));
            return;
          }
        }

        // Check if we have any data
        if (!results.data || !Array.isArray(results.data) || results.data.length === 0) {
          reject(new Error('CSV file is empty or has no valid data'));
          return;
        }

        // Get headers from the first row
        const firstRow = results.data[0];
        if (!firstRow || typeof firstRow !== 'object') {
          reject(new Error('Invalid CSV format: no headers found'));
          return;
        }

        const headers = Object.keys(firstRow);
        if (headers.length === 0) {
          reject(new Error('No headers found in CSV file'));
          return;
        }

        resolve(headers);
      },
      error: (error) => {
        console.error('CSV parsing error:', error);
        reject(new Error(`Failed to read CSV file: ${error.message}`));
      }
    });
  });
}

export async function parseCSV(
  file: File,
  mapping: ColumnMapping,
  onProgress?: (progress: number) => void
): Promise<Transaction[]> {
  return new Promise((resolve, reject) => {
    const transactions: Transaction[] = [];
    let processedRows = 0;
    let totalRows = 0;

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      step: (results) => {
        processedRows++;
        if (onProgress && totalRows > 0) {
          onProgress((processedRows / totalRows) * 100);
        }

        const row = results.data as Record<string, string>;
        if (!row || typeof row !== 'object') {
          console.warn('Invalid row data:', row);
          return;
        }

        try {
          const date = getRowValue(row, mapping.date);
          const description = getRowValue(row, mapping.description);
          const amount = getRowValue(row, mapping.amount);
          const type = getRowValue(row, mapping.type);
          const category = getRowValue(row, mapping.category) || 'Uncategorized';
          const balance = getRowValue(row, mapping.balance);
          const checkNumber = getRowValue(row, mapping.checkNumber);

          if (!date || !description || !amount) {
            console.warn('Missing required fields:', { date, description, amount });
            return;
          }

          const parsedDate = new Date(date);
          if (isNaN(parsedDate.getTime())) {
            console.warn('Invalid date:', date);
            return;
          }

          const parsedAmount = parseFloat(amount.replace(/[^0-9.-]+/g, ''));
          if (isNaN(parsedAmount)) {
            console.warn('Invalid amount:', amount);
            return;
          }

          // Determine transaction type if not provided
          let transactionType: 'debit' | 'credit' = type?.toLowerCase() === 'credit' ? 'credit' : 'debit';
          
          // If amount is negative, it's a debit; if positive, it's a credit
          if (!type) {
            transactionType = parsedAmount < 0 ? 'debit' : 'credit';
          }

          const transaction: Transaction = {
            id: crypto.randomUUID(),
            date: parsedDate.toISOString(),
            description: description || 'No Description',
            amount: Math.abs(parsedAmount), // Always store positive amount
            type: transactionType,
            category,
            balance: balance ? parseFloat(balance.replace(/[^0-9.-]+/g, '')) : undefined,
            checkNumber: checkNumber || undefined,
          };

          transactions.push(transaction);
        } catch (error) {
          console.error('Error processing row:', error);
        }
      },
      complete: () => {
        if (transactions.length === 0) {
          reject(new Error('No valid transactions found in file'));
          return;
        }
        resolve(transactions);
      },
      error: (error) => {
        reject(error);
      },
      beforeFirstChunk: (chunk) => {
        // Estimate total rows from first chunk
        const lines = chunk.split('\n');
        totalRows = lines.length - 1; // Subtract header row
      },
    });
  });
}

// Get column mappings from CSV headers
export function getColumnMappings(csvContent: string): Record<string, string> {
  const { data } = Papa.parse(csvContent, {
    header: true,
    preview: 1,
    transformHeader: (header) => header.trim(),
    transform: (value) => value.trim(),
  });

  if (!data || !Array.isArray(data) || data.length === 0) {
    throw new Error('No data found in CSV file');
  }

  const firstRow = data[0] as Record<string, string>;
  const headers = Object.keys(firstRow);
  const mappings: Record<string, string> = {};

  headers.forEach(header => {
    const normalized = normalizeHeader(header);
    if (normalized !== header) {
      mappings[header] = normalized;
    }
  });

  return mappings;
} 